/**
 * NumberField - Number input field component
 *
 * Task: T-P4-D2-F4b-4
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Uses type="text" with inputMode="decimal" for better control.
 * Allows partial typing (-, ., 1.) before validation on blur.
 */

import type { ChangeEvent } from 'react';
import type { NumberFieldProps } from './types';
import { FieldWrapper } from './FieldWrapper';
import styles from './fields.module.css';

/**
 * NumberField renders a text input optimized for number entry.
 */
export function NumberField({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: NumberFieldProps) {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Allow empty, digits, minus, and decimal point while typing
    // Validation happens on blur via formValidation.ts
    const newValue = e.target.value;
    // Only allow valid number-like characters
    if (/^-?\d*\.?\d*$/.test(newValue) || newValue === '') {
      onChange(newValue);
    }
  };

  return (
    <FieldWrapper
      fieldId={field.fieldId}
      label={field.label}
      required={field.required}
      helpText={field.helpText}
      error={error}
      style={style}
    >
      <input
        type="text"
        inputMode="decimal"
        id={field.fieldId}
        name={field.fieldId}
        className={`${styles.input} ${error ? styles.hasError : ''}`}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={field.placeholder}
        readOnly={nonInteractive}
        aria-invalid={!!error}
        aria-describedby={error ? `${field.fieldId}-error` : undefined}
      />
    </FieldWrapper>
  );
}
